import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { FamilySettingsConstants as ui, FRESHDESK } from '../../Config/UIConstants';
import { CommonActionButton } from '../../SharedComponents/CommonActionButton';
import { EmptyContentOnTabDisplay } from '../../SharedComponents/EmptyContentOnTabDisplay';
import { FullContainer } from '../../SharedComponents/FullContainer';
import { CollectionGrid } from '../../SharedComponents/GridDisplay/CollectionGrid';
import { InformationIcon } from '../../SharedComponents/InformationIcon';
import {
    ActiveTabState,
    NavigationContainer
} from '../../SharedComponents/MainPageWithNavigator/MainPageWithNavigator';
import { styles as navigatorStyles } from '../../SharedComponents/MainPageWithNavigator/NavigatorStyles';
import { IStory } from '../../Stores/StoryStore';
import { IUserFamily } from '../../Stores/UserFamilyStore';
import { Routes } from '../../_controllers/RouteController';
import { ApplicationStore } from '../../_stores/ApplicationStore';
import { PlaceholderStoryImage } from '../../images/images';

interface IProps {
    store: ApplicationStore;
}

interface IState {
    storyContent?: Array<IStory>;
}

@inject('store')
@observer
export class FamiliesPage extends React.Component<IProps & RouteComponentProps<any>> {
    public componentDidMount(): void {
        window.scrollTo(0, 0);
    }

    public state: IState = {
        storyContent: undefined
    };

    private onClickFamily = (family: IUserFamily) => {
        this.props.history.push(`/familydetail/${family.id}`);
    }

    private renderFamilyMemberCells = (family: IUserFamily) => {
        const memberCount = family.members.length;

        if (memberCount === 0) {
            return <img style={styles.oneCountContainer} src={PlaceholderStoryImage} />;
        }

        if (memberCount === 1) {
            return <img style={styles.oneCountContainer} src={family.members[0].profileImageUrl} />;
        }

        if (memberCount < 4) {
            return (
                <FullContainer style={styles.twoCountContainer}>
                    <img src={family.members[0].profileImageUrl} style={styles.halfWidthImage} />
                    <img src={family.members[1].profileImageUrl} style={styles.halfWidthImage} />
                    {memberCount === 3 && (
                        <div style={styles.twoCountExtraBG}>
                            <p className="TextShadow" style={styles.extraMemberCount}>
                                {ui.PLUS_ONE}
                            </p>
                        </div>
                    )}
                </FullContainer>
            );
        }

        if (memberCount < 6) {
            return (
                <FullContainer style={styles.fourCountContainer}>
                    <div style={styles.memberRow}>
                        <img src={family.members[0].profileImageUrl} style={styles.halfWidthImage} />
                        <img src={family.members[1].profileImageUrl} style={styles.halfWidthImage} />
                    </div>

                    <div style={styles.memberRow}>
                        <img src={family.members[2].profileImageUrl} style={styles.halfWidthImage} />
                        <img src={family.members[3].profileImageUrl} style={styles.halfWidthImage} />
                    </div>
                    {memberCount === 5 && (
                        <div style={styles.fourCountExtraBG}>
                            <p className="TextShadow" style={styles.extraMemberCount}>
                                {ui.PLUS_ONE}
                            </p>
                        </div>
                    )}
                </FullContainer>
            );
        }

        return (
            <FullContainer style={styles.sixCountContainer}>
                <div style={styles.memberRow}>
                    <img src={family.members[0].profileImageUrl} style={styles.sixCountImage} />
                    <img src={family.members[1].profileImageUrl} style={styles.sixCountImage} />
                    <img src={family.members[2].profileImageUrl} style={styles.sixCountImage} />
                </div>

                <div style={styles.memberRow}>
                    <img src={family.members[3].profileImageUrl} style={styles.sixCountImage} />
                    <img src={family.members[4].profileImageUrl} style={styles.sixCountImage} />
                    <img src={family.members[5].profileImageUrl} style={styles.sixCountImage} />
                </div>
                {memberCount > 6 && (
                    <div style={styles.sixCountExtraBG}>
                        <p className="TextShadow" style={styles.extraMemberCount}>
                            {'+' + (family.members.length - 6)}
                        </p>
                    </div>
                )}
            </FullContainer>
        );
    }

    private renderItem = (family: IUserFamily) => {
        return (
            <FullContainer style={styles.relative} onClick={() => this.onClickFamily(family)}>
                {this.renderFamilyMemberCells(family)}
                <div style={styles.cardTextContainer}>
                    <p className="TextShadow" style={styles.familyNameText}>
                        {family.name}
                    </p>
                    <p className="TextShadow" style={styles.memberCountText}>
                        {ui.MEMBER_COUNT(family.members.length)}
                    </p>
                </div>
            </FullContainer>
        );
    }

    public render() {
        if (!this.props.store.userFamilyStore || !this.props.store.userFamilyStore.familiesAreLoaded) {
            return <NavigationContainer {...this.props} activeTabState={ActiveTabState.FAMILIES} />;
        }

        const actionButtons = <React.Fragment>
            <InformationIcon
                link={FRESHDESK.ADD_FAMILY}
                style={navigatorStyles.actionButtonInfoIcon}/>
            <CommonActionButton
                buttonContent="Create Family"
                onClick={() => this.props.history.push(Routes.ADD_FAMILY)}
                containerStyle={{...navigatorStyles.buttonContainer, ...navigatorStyles.actionButtonWhenInfoIconPresent}}
                fontStyle={navigatorStyles.buttonText}
            />
        </React.Fragment>;

        return (
            <NavigationContainer
                {...this.props}
                activeTabState={ActiveTabState.FAMILIES}
                actionButtonAreaChildren={actionButtons}
            >
                {this.props.store.userFamilyStore.userFamilies.length === 0 ? (
                    <EmptyContentOnTabDisplay
                        header={ui.NO_FAMILY}
                        subHeader={ui.CREATE_A_FAMILY}
                        buttonText="Create Family"
                        onPress={() => this.props.history.push(Routes.ADD_FAMILY)}
                    />
                ) : (
                    <FullContainer>
                        <CollectionGrid content={this.props.store.userFamilyStore.userFamilies} renderItem={this.renderItem} />
                    </FullContainer>
                )}
            </NavigationContainer>
        );
    }
}

const styles = {
    sixCountContainer: {
        backgroundColor: 'black',
        position: 'relative'
    } as React.CSSProperties,
    sixCountImage: {
        display: 'flex',
        width: '33.333333%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'center'
    } as React.CSSProperties,
    memberRow: {
        height: '50%',
        width: '100%',
        display: 'flex'
    },
    fourCountContainer: {
        backgroundColor: 'black',
        position: 'relative'
    } as React.CSSProperties,
    oneCountContainer: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'center'
    } as React.CSSProperties,
    twoCountContainer: {
        display: 'flex',
        backgroundColor: 'black',
        position: 'relative'
    } as React.CSSProperties,
    twoCountExtraBG: {
        height: '100%',
        width: '50%',
        backgroundColor: '#00000022',
        right: 0,
        bottom: 0,
        top: 0,
        position: 'absolute',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 100
    } as React.CSSProperties,
    fourCountExtraBG: {
        height: '50%',
        width: '50%',
        backgroundColor: '#00000022',
        right: 0,
        bottom: 0,
        position: 'absolute',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 100
    } as React.CSSProperties,
    sixCountExtraBG: {
        height: '50%',
        width: '33.33333%',
        backgroundColor: '#00000022',
        right: 0,
        bottom: 0,
        position: 'absolute',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 100
    } as React.CSSProperties,
    extraMemberCount: {
        fontSize: 48,
        color: 'white',
        textAlign: 'center'
    } as React.CSSProperties,
    cardTextContainer: {
        position: 'absolute',
        bottom: 0,
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        backgroundColor: '#00000022',
        paddingLeft: 20,
        paddingBottom: 15
    } as React.CSSProperties,
    familyNameText: {
        color: 'white',
        marginTop: 4,
        fontSize: 20,
        letterSpacing: 0.5
    },
    memberCountText: {
        color: 'white',
        marginTop: 4,
        fontSize: 14
    },
    relative: {
        position: 'relative',
        backgroundColor: 'grey',
        cursor: 'pointer'
    } as React.CSSProperties,
    halfWidthImage: {
        display: 'flex',
        width: '50%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: 'center'
    } as React.CSSProperties
};



// WEBPACK FOOTER //
// ./src/Pages/FamiliesPage/FamiliesPage.tsx